import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ExportDownloadResponse } from '@api-response-types/export-download.response';
import { FilterValue } from '@api-types/types/filter-value.type';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { triggerLinkDownload } from 'common/export-helper';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, firstValueFrom, map, Observable } from 'rxjs';
import { ConsensusExportStateService } from './consensus-export-state.service';

export type StartConsensusExportInput = {
  configurationId: string;
  consensusTemplateId: string;
  format: 'CSV' | 'XLSX';
  /** Value-cell decimal places (0–30). Mirrors the forecast export. */
  decimalDigits?: number;
  /** Ordered planning-hierarchy prefix to aggregate to; omitted ⇒ leaf export. */
  aggregationColumns?: string[];
  /** Active applied dimension filters to scope the export to (opt-in). */
  rawFilters?: Record<string, FilterValue[]>;
  /** Saved quick-filter combination ids, resolved + merged server-side. */
  filterCombinationIds?: string[];
  /**
   * First month (`yyyy-MM`) of the picked range when it reaches before the
   * forecast horizon — the export then carries the same Ist columns as the
   * table. Omitted ⇒ horizon only.
   */
  historyStartMonth?: string;
};

@Injectable({ providedIn: 'root' })
export class ConsensusExportService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly stateService = inject(ConsensusExportStateService);

  /** True while a `POST /consensus/exports` is in flight — bound to the
   *  primary CTA's disabled state in the NEW tab. */
  public readonly startExportLoading = signal(false);

  /**
   * Dispatches a new consensus export. Shows a toast on accept; the row
   * appears in the LIST tab immediately because the dispatcher inserts the
   * `ExportModel` row as `PENDING` synchronously. SSE-driven status
   * transitions are handled by `ConsensusExportSseHandler`.
   */
  startExport(
    input: StartConsensusExportInput,
  ): Observable<{ exportId: string }> {
    this.startExportLoading.set(true);
    return this.http
      .post<{
        exportId: string;
      }>(`${environment.api.url}/consensus/exports`, input)
      .pipe(
        map((response) => {
          this.toast.success(
            this.translate.instant('CONSENSUS.EXPORT.TOAST_STARTED'),
          );
          // ensure the freshly-inserted PENDING row shows in the LIST tab
          this.stateService.exports.reload();
          return response;
        }),
        catchError((err) => {
          this.toast.error(this.translate.instant('CONSENSUS.EXPORT.FAILED'));
          throw err;
        }),
        finalize(() => this.startExportLoading.set(false)),
      );
  }

  /**
   * Mints a fresh 30 s SAS via the existing forecast download endpoint
   * (data-agnostic — keyed by blob name only) and triggers the browser
   * download via a temporary anchor.
   */
  async downloadSelectedExport(exportId: string): Promise<void> {
    if (!exportId) {
      console.error('No export selected when trying to download');
      return;
    }
    try {
      const downloadData = await firstValueFrom(
        this.http.get<ExportDownloadResponse>(
          `${environment.api.url}/export/download/${exportId}`,
        ),
      );
      triggerLinkDownload(downloadData.downloadLink, downloadData.fileName);
    } catch {
      this.toast.error(
        this.translate.instant('CONSENSUS.EXPORT.DOWNLOAD_FAILED'),
      );
    }
  }
}
